import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { usePageChat } from '@/hooks';
import { ChatInput } from '../components/chat/ChatInput';
import { QuickSuggestionsBlock } from '../components/chat/QuickSuggestionsBlock';
import { Header } from '@components/layout';
import { Footer } from '@components/layout';
import { ChatContainer } from '../components/chat/ChatContainer';
import { LoadingState } from '../components/common/LoadingState';
import { SchemaMarkup } from '@/components/common/SchemaMarkup';
import { CanonicalTag } from '@/components/common/CanonicalTag';
import { getComparisons, getComparisonsByGroup } from '@/config/comparisons.config';
import { getVehicles } from '@/config/vehicles.config';

interface CompareIndexPageProps {
    showBanner: () => void;
}

const GROUP_CONFIG: Record<string, { label: string; emoji: string; description: string }> = {
    trucks: {
        label: 'Pickup Truck Comparisons',
        emoji: '🚛',
        description: 'Pickup trucks are among the most commonly compared vehicles because drivers evaluate towing capability, payload capacity, and durability.',
    },
    suvs: {
        label: 'Compact SUV Comparisons',
        emoji: '🚙',
        description: 'Compact SUVs are one of the most popular vehicle categories. Drivers often compare SUVs when researching family vehicles, winter driving capability, and long-term reliability.',
    },
    cars: {
        label: 'Sedan and Compact Car Comparisons',
        emoji: '🚗',
        description: 'Compact cars and sedans are commonly compared by drivers looking for fuel-efficient and reliable vehicles.',
    },
};

const faqItems = [
    {
        question: 'What is the best way to compare vehicles?',
        answer: 'The best way to compare vehicles is to evaluate performance, fuel efficiency, reliability, and features side by side. Comparing similar vehicle categories helps narrow down options.',
    },
    {
        question: 'Which vehicles are most commonly compared?',
        answer: 'Drivers commonly compare vehicles within the same category, such as Honda CR-V vs Toyota RAV4, Ford F-150 vs Chevrolet Silverado, and Honda Civic vs Toyota Corolla.',
    },
    {
        question: 'Should I compare vehicles before visiting a dealership?',
        answer: 'Yes. Comparing vehicles helps drivers understand differences before visiting a dealership, making it easier to choose the right model and ask informed questions.',
    },
    {
        question: 'How many vehicles should I compare?',
        answer: 'Most drivers compare 2–3 vehicles at a time to make a decision. Comparing too many options can make the process more difficult.',
    },
];

export const CompareIndexPage: React.FC<CompareIndexPageProps> = ({ showBanner }) => {
    const navigate = useNavigate();
    const { messages, isLoading, streamingMessage, sendMessage, assistant, loadingAssistants, messagesEndRef, currentTool } = usePageChat();
    const comparisonsByGroup = getComparisonsByGroup();

    const featuredVehicles = [
        { name: 'Ford F-150', path: '/vehicles/ford/f-150' },
        { name: 'Chevrolet Silverado', path: '/vehicles/chevrolet/silverado' },
        { name: 'Honda CR-V', path: '/vehicles/honda/cr-v' },
        { name: 'Toyota RAV4', path: '/vehicles/toyota/rav4' },
        { name: 'Nissan Rogue', path: '/vehicles/nissan/rogue' },
        { name: 'Honda Civic', path: '/vehicles/honda/civic' },
    ];

    const chatSuggestions = [
        'Show vehicles available near me',
        'Compare SUVs near me',
        'Find pickup trucks nearby',
        'Browse fuel-efficient cars near me',
    ];

    const faqSchema = {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqItems.map(item => ({
            '@type': 'Question',
            name: item.question,
            acceptedAnswer: { '@type': 'Answer', text: item.answer },
        })),
    };

    if (loadingAssistants || !assistant) {
        return (
            <>
                <Header />
                <div className="pt-20 sm:pt-24 md:pt-28 pb-28 px-2 sm:px-4">
                    <div className="max-w-3xl mx-auto"><LoadingState message="Loading..." /></div>
                </div>
                <Footer />
            </>
        );
    }

    return (
        <>
            <CanonicalTag href="https://getdrivesource.com/compare" />
            <SchemaMarkup schema={faqSchema} />
            <Header />
            <div className="pt-20 sm:pt-24 md:pt-28 pb-28 sm:pb-32 md:pb-36 px-2 sm:px-3 md:px-4">
                <div className="max-w-3xl mx-auto">
                    <div className="min-h-[60vh] space-y-2 sm:space-y-3 md:space-y-4 py-2 sm:py-3 md:py-4">
                        <article className="mb-6">

                            {/* H1 */}
                            <h1 className="text-lg sm:text-2xl md:text-3xl font-bold text-white mb-2">
                                Vehicle Comparisons
                                <span className="block text-sm sm:text-base md:text-lg font-medium text-gray-400 mt-1">
                                    Compare Cars, SUVs, and Trucks
                                </span>
                            </h1>

                            {/* Intro */}
                            <div className="mb-4">
                                <h2 className="text-base sm:text-xl font-bold text-white mt-4 sm:mt-6 mb-2 sm:mb-3">
                                    Compare Vehicles to Find the Right Car, SUV, or Truck
                                </h2>
                                <p className="text-sm sm:text-base text-gray-300 mb-3">
                                    Drivers often compare vehicles before deciding which model best fits their needs. Comparing vehicles helps evaluate differences in performance, fuel efficiency, interior features, reliability, and long-term ownership.
                                </p>
                                <p className="text-sm sm:text-base text-gray-300 mb-2">Drivers commonly compare vehicles such as:</p>
                                <ul className="list-disc list-inside space-y-1.5 text-sm sm:text-base text-gray-300 ml-2">
                                    {featuredVehicles.map((v, i) => (
                                        <li key={i}>
                                            <Link to={v.path} className="text-cyan-400 hover:text-cyan-300 underline transition-colors">
                                                {v.name}
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            {/* Entity Definition */}
                            <div className="mb-4">
                                <h2 className="text-base sm:text-xl font-bold text-white mt-4 sm:mt-6 mb-2 sm:mb-3">
                                    Vehicle Comparison — What It Means
                                </h2>
                                <p className="text-sm sm:text-base text-gray-300 mb-2">
                                    A vehicle comparison is the process of evaluating two or more vehicles based on performance, features, reliability, and cost to determine which model best fits a driver's needs.
                                </p>
                                <p className="text-sm sm:text-base text-gray-300 mb-2">Vehicle comparisons commonly include:</p>
                                <ul className="list-disc list-inside space-y-1 text-sm sm:text-base text-gray-300 ml-2">
                                    <li>side-by-side feature comparisons</li>
                                    <li>performance and capability differences</li>
                                    <li>ownership cost considerations</li>
                                    <li>use-case recommendations for families, towing, and commuting</li>
                                </ul>
                            </div>

                            {/* Groups */}
                            {(['trucks', 'suvs', 'cars'] as const).map(group => {
                                const groupComparisons = comparisonsByGroup[group] || [];
                                const config = GROUP_CONFIG[group];
                                if (groupComparisons.length === 0) return null;
                                return (
                                    <div key={group} className="mb-4">
                                        <h2 className="text-base sm:text-xl font-bold text-white mt-4 sm:mt-6 mb-2 sm:mb-3">
                                            {config.label}
                                        </h2>
                                        <p className="text-sm sm:text-base text-gray-300 mb-3">{config.description}</p>
                                        <ul className="list-disc list-inside space-y-1.5 sm:space-y-2 text-sm sm:text-base text-gray-300 ml-2">
                                            {groupComparisons.map((c, i) => (
                                                <li key={i}>
                                                    <Link
                                                        to={`/compare/${c.slug}`}
                                                        className="text-cyan-400 hover:text-cyan-300 underline transition-colors"
                                                    >
                                                        {c.vehicle1.name} vs {c.vehicle2.name}
                                                    </Link>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                );
                            })}

                            {/* How to Compare */}
                            <div className="mb-4">
                                <h2 className="text-base sm:text-xl font-bold text-white mt-4 sm:mt-6 mb-2 sm:mb-3">
                                    How to Compare Vehicles
                                </h2>
                                <p className="text-sm sm:text-base text-gray-300 mb-2">Drivers comparing vehicles often consider:</p>
                                <ul className="list-disc list-inside space-y-1 text-sm sm:text-base text-gray-300 ml-2">
                                    <li>reliability and long-term ownership</li>
                                    <li>fuel efficiency and operating costs</li>
                                    <li>interior features and technology</li>
                                    <li>cargo space and passenger comfort</li>
                                    <li>towing capability for trucks and SUVs</li>
                                    <li>driving experience</li>
                                </ul>
                            </div>

                            {/* FAQ */}
                            <div className="mb-4">
                                <h2 className="text-base sm:text-xl font-bold text-white mt-4 sm:mt-6 mb-2 sm:mb-3">
                                    Frequently Asked Questions About Vehicle Comparisons
                                </h2>
                                <div className="space-y-4">
                                    {faqItems.map((item, i) => (
                                        <div key={i}>
                                            <h3 className="text-sm sm:text-base font-semibold text-gray-200 mb-1">{item.question}</h3>
                                            <p className="text-sm sm:text-base text-gray-300">{item.answer}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Find Near You */}
                            <QuickSuggestionsBlock
                                topLine="Ready to find your vehicle?"
                                title="Find Vehicles Near You"
                                suggestions={chatSuggestions}
                                onSend={sendMessage}
                            />

                        </article>

                        {messages.length > 0 && (
                            <ChatContainer
                                messages={messages}
                                assistant={assistant}
                                isLoading={isLoading}
                                streamingMessage={streamingMessage}
                                currentTool={currentTool}
                            />
                        )}
                        <div ref={messagesEndRef} />
                    </div>
                </div>
            </div>
            <ChatInput
                assistant={assistant}
                onSendMessage={sendMessage}
                disabled={isLoading}
                showBanner={showBanner}
            />
        </>
    );
};
